import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarCheck,
  CheckCircle2,
  CreditCard,
  Lock,
  PiggyBank,
  Plane,
  Receipt,
  ShieldCheck,
  Sparkles,
  Wallet,
} from "lucide-react";

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

const staggerContainer = {
  initial: {},
  whileInView: {
    transition: { staggerChildren: 0.1 },
  },
};

const capabilities = [
  { icon: <CreditCard className="w-5 h-5" />, title: "Paying bills" },
  { icon: <Plane className="w-5 h-5" />, title: "Planning travel" },
  { icon: <CalendarCheck className="w-5 h-5" />, title: "Managing reminders" },
  { icon: <Wallet className="w-5 h-5" />, title: "Tracking subscriptions" },
  { icon: <Receipt className="w-5 h-5" />, title: "Organizing schedules" },
  { icon: <PiggyBank className="w-5 h-5" />, title: "Finding the best deals" },
];

const steps = [
  {
    number: "01",
    title: "Tell Josh what you need",
    description: "Type or speak naturally. No complex setup or app hopping required.",
    example: "“Josh, pay my hydro bill.”",
  },
  {
    number: "02",
    title: "Josh finds the information",
    description: "Josh securely checks your saved accounts, services, and preferences.",
    example: "Saved payees, travel preferences, reminders, and account details.",
  },
  {
    number: "03",
    title: "Josh gets it done",
    description: "Josh confirms the action and completes the task for you.",
    example: "Clear confirmation before anything important happens.",
  },
];

const exampleGroups = [
  {
    title: "Bills",
    prompt: "“Josh, pay my electricity bill.”",
    icon: <CreditCard className="w-5 h-5" />,
  },
  {
    title: "Travel",
    prompt: "“Find me a hotel in Rome under $200.”",
    icon: <Plane className="w-5 h-5" />,
  },
  {
    title: "Reminders",
    prompt: "“Remind me to call the dentist tomorrow at 11 am.”",
    icon: <CalendarCheck className="w-5 h-5" />,
  },
  {
    title: "Subscriptions",
    prompt: "“What subscriptions am I paying for?”",
    icon: <Wallet className="w-5 h-5" />,
  },
  {
    title: "Daily help",
    prompt: "“Plan my weekend trip.”",
    icon: <Sparkles className="w-5 h-5" />,
  },
];

const differentiators = [
  "Most assistants give you answers. Josh takes action.",
  "Josh connects with services you already use.",
  "Josh helps automate routine tasks and keep life organized.",
  "Josh is built to save time every day, not add more steps.",
];

const trustPoints = [
  {
    icon: <Lock className="w-5 h-5" />,
    title: "Secure by design",
    description: "Your information is protected with secure encryption and trusted infrastructure.",
  },
  {
    icon: <ShieldCheck className="w-5 h-5" />,
    title: "Approval-first actions",
    description: "Josh only acts on your behalf when you approve.",
  },
  {
    icon: <CheckCircle2 className="w-5 h-5" />,
    title: "You stay in control",
    description: "Your data remains under your control at every step.",
  },
];

const JoshPage = () => {
  return (
    <div className="min-h-screen pt-20 bg-white" data-testid="josh-page">
      <section className="min-h-[calc(100vh-5rem)] flex items-center py-16 md:py-24">
        <div className="container-main">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="grid grid-cols-1 lg:grid-cols-[1.15fr_0.85fr] gap-10 lg:gap-16 items-center"
          >
            <div className="max-w-4xl">
              <p className="caption mb-4">Introducing Josh</p>
              <h1 className="heading-1 mb-6">
                Meet Josh {" "}
                <span className="bg-gradient-to-r from-purple-600 via-fuchsia-500 to-pink-500 bg-clip-text text-transparent">
                  your AI that handles life&apos;s tasks.
                </span>
              </h1>
              <p className="body-lg max-w-3xl mb-6">
                Stop juggling apps. Just tell Josh what you need and it gets it done.
              </p>
              <p className="body-text max-w-2xl mb-10">
                Josh can help you pay bills, plan travel, manage reminders, and handle
                everyday tasks from a single conversation.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="btn-primary inline-flex items-center justify-center gap-2"
                  data-testid="josh-hero-cta"
                >
                  Try Josh
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/contact"
                  className="btn-secondary inline-flex items-center justify-center"
                  data-testid="josh-hero-secondary-cta"
                >
                  Get Early Access
                </Link>
              </div>
            </div>

            <motion.div
              {...fadeInUp}
              className="relative rounded-[2.5rem] bg-[#F5F5F7] border border-zinc-100 p-8 md:p-10 overflow-hidden"
            >
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(217,70,239,0.14),transparent_35%),radial-gradient(circle_at_bottom_left,rgba(147,51,234,0.1),transparent_30%)]" />
              <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-zinc-700 shadow-sm">
                  <Sparkles className="w-4 h-4 text-fuchsia-500" />
                  Your digital personal assistant
                </div>
                <div className="mt-8 space-y-4">
                  {[
                    "Pay my hydro bill.",
                    "Find me a hotel in Rome under $200.",
                    "What subscriptions am I paying for?",
                    "Plan my weekend trip.",
                  ].map((item) => (
                    <div
                      key={item}
                      className="rounded-2xl bg-white p-4 text-sm md:text-base text-zinc-700 shadow-[0_10px_30px_rgba(0,0,0,0.04)]"
                    >
                      {item}
                    </div>
                  ))}
                </div>
                <p className="mt-6 text-sm text-zinc-500">
                  One conversation. Real tasks handled.
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">What Josh Does</p>
            <h2 className="heading-2 mb-6">Your life, simplified.</h2>
            <p className="body-text">
              Josh is an AI assistant designed to save you time on everyday tasks.
              Instead of opening multiple apps, simply tell Josh what you want and it
              handles the rest.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {capabilities.map((item) => (
              <motion.div key={item.title} variants={fadeInUp} className="feature-card flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600">
                  {item.icon}
                </div>
                <span className="text-lg font-medium text-zinc-900">{item.title}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">How It Works</p>
            <h2 className="heading-2 mb-6">Simple by design.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-6"
          >
            {steps.map((step) => (
              <motion.div key={step.number} variants={fadeInUp} className="feature-card">
                <p className="text-sm font-semibold tracking-[0.2em] text-zinc-400 mb-5">{step.number}</p>
                <h3 className="heading-3 mb-4">{step.title}</h3>
                <p className="body-text mb-5">{step.description}</p>
                <div className="rounded-2xl bg-[#F5F5F7] px-5 py-4 text-sm text-zinc-600">
                  {step.example}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Examples</p>
            <h2 className="heading-2 mb-6">What you can ask Josh.</h2>
            <p className="body-text">
              Josh handles the details so you don&apos;t have to.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-6"
          >
            {exampleGroups.map((group) => (
              <motion.div key={group.title} variants={fadeInUp} className="feature-card">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center text-zinc-600 mb-5">
                  {group.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{group.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600">{group.prompt}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <motion.div {...fadeInUp}>
              <p className="caption mb-4">Why Josh Is Different</p>
              <h2 className="heading-2 mb-6">Josh takes action.</h2>
              <p className="body-text mb-8">
                Think of Josh as your digital personal assistant. Built to automate
                routine tasks, keep you organized, and save time every day.
              </p>
              <div className="space-y-4">
                {differentiators.map((point) => (
                  <div key={point} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 mt-1 text-zinc-500 flex-shrink-0" />
                    <p className="body-text">{point}</p>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div {...fadeInUp} className="feature-card">
              <p className="caption mb-4">Save Time Every Day</p>
              <h3 className="heading-3 mb-4">Less admin. More life.</h3>
              <p className="body-text mb-6">
                The average person spends hours each week managing bills, scheduling
                appointments, tracking subscriptions, and searching for information.
                Josh helps automate those tasks so you can focus on what matters.
              </p>
              <div className="rounded-[1.5rem] bg-[#F5F5F7] p-6">
                <p className="text-sm uppercase tracking-[0.2em] text-zinc-400 mb-3">
                  The goal
                </p>
                <p className="text-xl sm:text-2xl font-medium tracking-tight text-zinc-900">
                  A future where you don&apos;t need dozens of apps to manage your life.
                </p>
                <p className="mt-4 text-base text-zinc-600">
                  You just tell one assistant: &ldquo;Josh, handle it.&rdquo;
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-[#F5F5F7]">
        <div className="container-main">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto mb-16">
            <p className="caption mb-4">Privacy and Security</p>
            <h2 className="heading-2 mb-6">Built for trust.</h2>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="whileInView"
            viewport={{ once: true }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {trustPoints.map((point) => (
              <motion.div key={point.title} variants={fadeInUp} className="feature-card text-center">
                <div className="w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center mx-auto mb-5 text-zinc-600">
                  {point.icon}
                </div>
                <h3 className="text-xl font-medium text-zinc-900 mb-3">{point.title}</h3>
                <p className="text-sm leading-relaxed text-zinc-600">{point.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="section-spacing bg-zinc-900">
        <div className="container-main text-center">
          <motion.div {...fadeInUp} className="max-w-3xl mx-auto">
            <p className="caption text-zinc-500 mb-4">Early Access</p>
            <h2 className="heading-2 text-white mb-6">Join early access for Josh.</h2>
            <p className="text-lg text-zinc-400 leading-relaxed mb-10">
              Josh is currently in early development. Join the early access list to be
              among the first to try it. Josh is built by The App Fam as part of our
              mission to make technology simpler, smarter, and more helpful in everyday life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="h-12 px-8 rounded-full bg-white text-zinc-900 font-medium text-sm hover:bg-zinc-100 transition-all inline-flex items-center justify-center gap-2"
                data-testid="josh-early-access-cta"
              >
                Get Early Access
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/apps"
                className="h-12 px-8 rounded-full border border-zinc-700 text-white font-medium text-sm hover:bg-zinc-800 transition-all inline-flex items-center justify-center"
                data-testid="josh-apps-cta"
              >
                Explore The App Fam
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default JoshPage;
